import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  fetchNotifications,
  markNotificationAsRead,
  markNotificationAsUnread,
} from "@core/actions/NotificationActions.ts";
import { useNavigate } from "react-router-dom";
import NotifSvg from "@modules/notification/notif_Svg.tsx";
import {
  NotificationTypeEnum,
  NotificationTypeEnumList,
  UserNotification,
} from "@core/modeles/Notifimodels.ts";
import { dateTime, getNotificationNameFromCode } from "@utils/appUtils.ts";
import { classNames } from "primereact/utils";
import {
  Search,
  CheckCircle2,
  Circle,
  FolderOpen,
  Check,
  X,
  AlertTriangle,
  ArrowRightLeft,
  Bell,
  Filter,
  ChevronDown,
  FileText,
} from "lucide-react";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Nullable } from "primereact/ts-helpers.js";
import { Client } from "@stomp/stompjs";
import { getTokenKeycloak } from "@core/keycloak/keycloak-config.ts";

// const WS_URL = "ws://localhost:8081/api/ws-notification";
const WS_URL = `${process.env.REACT_APP_WS_PROTOCOL}://${process.env.REACT_APP_WS_DOMAIN}${process.env.REACT_APP_WS_PATH}`;
// console.log("Connecting to WebSocket:", WS_URL);

const NotificationAdminPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const notifications: UserNotification[] = useAppSelector(
    (state) => state.notification_admin.list
  );
  const totalUnread = notifications.filter((n) => !n.read).length;

  const [visible, setVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState<Nullable<Date>>(null);

  const [typeFilter, setTypeFilter] = useState<"all" | NotificationTypeEnum>(
    "all"
  );
  const [statusFilter, setStatusFilter] = useState<"all" | "read" | "unread">(
    "all"
  );
  const [type, setType] = useState<"all" | NotificationTypeEnum>("all");
  const [status, setStatus] = useState<"all" | "read" | "unread">("all");
  const [showFilters, setShowFilters] = useState(false);
  const dialogRef = useRef<Dialog>(null);
  const stompClient = useRef<any>(null);

  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch]);

  useEffect(() => {
    let oldToken: string | null = null;
    let intervalId: NodeJS.Timeout;

    const setupStompClient = async (token: string) => {
      if (stompClient.current) {
        await stompClient.current.deactivate();
      }
      stompClient.current = new Client({
        brokerURL: WS_URL,
        connectHeaders: {
          Authorization: `Bearer ${token}`,
        },
        reconnectDelay: 5000,
        onConnect: () => {
          stompClient.current.subscribe(
            "/topic/notifications",
            handleTopicNotification
          );
          stompClient.current.subscribe(
            "/user/queue/notifications",
            handleUserNotification
          );
          stompClient.current.subscribe("/user/queue/ack");
        },
      });
      stompClient.current.activate();
    };

    const handleTopicNotification = () => {
      dispatch(fetchNotifications());
    };

    const handleUserNotification = () => {
      dispatch(fetchNotifications());
    };

    const refreshAndReconnect = async () => {
      try {
        const newToken = await getTokenKeycloak();

        if (!oldToken || newToken !== oldToken) {
          oldToken = newToken;
          await setupStompClient(newToken!);
        }
      } catch (error) {
        console.warn(error);
      }
    };

    refreshAndReconnect();
    intervalId = setInterval(refreshAndReconnect, 10000);

    return () => {
      clearInterval(intervalId);
      if (stompClient.current) {
        stompClient.current.deactivate();
      }
    };
  }, [dispatch]);

  const onOpen = () => setVisible(true);

  const onNotificationClick = (id: number, link: string, read: boolean) => {
    if (!read) {
      dispatch(markNotificationAsRead(id));
    }
    setVisible(false);
    navigate(link);
  };

  const getEventIcon = (type: NotificationTypeEnum) => {
    switch (type) {
      case "ASSIGNMENT":
        return <FolderOpen className="w-5 h-5 text-blue-500" />;
      case "VALIDATION":
        return <Check className="w-5 h-5 text-green-500" />;
      case "REJECTION":
        return <X className="w-5 h-5 text-red-500" />;
      case "CRITICAL_ALERT":
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case "TRANSFER":
        return <ArrowRightLeft className="w-5 h-5 text-purple-500" />;
      case "NEW_COMPLAINT":
        return <FileText className="w-5 h-5 text-pink-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getNotificationStyle = (type: NotificationTypeEnum) => {
    switch (type) {
      case NotificationTypeEnum.ASSIGNMENT:
        return "border-l border-0 border-blue-500";
      case NotificationTypeEnum.VALIDATION:
        return "border-l border-0 border-green-500";
      case NotificationTypeEnum.REJECTION:
        return "border-l border-0 border-red-500";
      case NotificationTypeEnum.CRITICAL_ALERT:
        return "border-l border-0 border-red-600";
      case NotificationTypeEnum.TRANSFER:
        return "border-l border-0 border-purple-500";
      case NotificationTypeEnum.NEW_COMPLAINT:
        return "border-l border-0 border-pink-500";
      default:
        return "border-l border-0 border-gray-300";
    }
  };

  const filteredNotifications = useMemo(() => {
    return notifications
      .filter((notif) => {
        const notificationDate = new Date(notif.notificationDto.createdAt);
        const matchesSearch =
          notif.notificationDto.name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          notif.notificationDto.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        const matchesType =
          typeFilter === "all" || notif.notificationDto.type === typeFilter;
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "read" && notif.read) ||
          (statusFilter === "unread" && !notif.read);

        let matchesDate = true;
        if (dateFilter) {
          const filterDate = new Date(dateFilter);
          const notifDateOnly = new Date(
            notificationDate.getFullYear(),
            notificationDate.getMonth(),
            notificationDate.getDate()
          );
          const filterDateOnly = new Date(
            filterDate.getFullYear(),
            filterDate.getMonth(),
            filterDate.getDate()
          );
          matchesDate = notifDateOnly.getTime() === filterDateOnly.getTime();
        }

        return matchesSearch && matchesType && matchesStatus && matchesDate;
      })
      .sort(
        (a, b) =>
          new Date(b.notificationDto.createdAt).getTime() -
          new Date(a.notificationDto.createdAt).getTime()
      );
  }, [notifications, searchTerm, typeFilter, statusFilter, dateFilter]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="notification-section">
      <div className="relative cursor-pointer" onClick={onOpen}>
        <NotifSvg />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full shadow">
            {totalUnread}
          </span>
        )}
      </div>

      <Dialog
        visible={visible}
        onHide={() => setVisible(false)}
        style={{
          position: "absolute",
          top: "64px",
          height: "calc(100vh - 64px)",
        }}
        className="notification-dialog w-[90vw] lg:w-[80vw] left-auto"
        modal
        closable
        draggable={false}
        ref={dialogRef}
        headerClassName="py-2 px-4 bg-white border-b border-[#D9D9D9]"
        contentClassName="p-0"
        header={
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-semibold text-gray-900">
                  Notifications
                </h1>
                {unreadCount > 0 && (
                  <span className="bg-red-500 flex items-center justify-center text-white text-xs font-semibold w-6 h-6 p-3 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
              {/* <button
                // onClick={markAllAsRead}
                disabled={unreadCount === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Tout marquer comme lu</span>
              </button> */}
            </div>

            <div className="">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher dans les notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Filter className="w-4 h-4" />
                  <span>Filtres</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      showFilters ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>

              {showFilters && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type
                      </label>
                      <Dropdown
                        value={type}
                        onChange={(e: DropdownChangeEvent) => {
                          setTypeFilter(e.value.code);
                          setType(e.value);
                        }}
                        options={[
                          { code: "all", name: "Tous les types" },
                          ...NotificationTypeEnumList,
                        ]}
                        optionLabel="name"
                        placeholder="Selectionner le type"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Statut
                      </label>
                      <Dropdown
                        value={status}
                        onChange={(e: DropdownChangeEvent) => {
                          setStatusFilter(e.value.code);
                          setStatus(e.value);
                        }}
                        options={[
                          { code: "all", name: "Toutes" },
                          { code: "unread", name: "Non lues" },
                          { code: "read", name: "Lues" },
                        ]}
                        optionLabel="name"
                        placeholder="Selectionner le statut"
                        className="w-full"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date
                      </label>
                      <Calendar
                        id="buttondisplay"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.value)}
                        className="w-full"
                        showIcon
                      />

                      {dateFilter && (
                        <Button
                          onClick={() => setDateFilter(null)}
                          className="mt-1 text-xs text-blue-600 hover:text-blue-800 absolute top-10 right-12 z-10"
                        >
                          Effacer le filtre date
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        }
      >
        <div style={{ maxHeight: "calc(100vh - 150px)" }}>
          {filteredNotifications.length === 0 ? (
            <div className="text-center text-gray-500 mt-4">
              Aucune notification
            </div>
          ) : (
            filteredNotifications.map(({ id, notificationDto, read }) => (
              <div
                key={id}
                className={classNames(
                  `p-6 hover:bg-gray-50 transition-colors cursor-pointer group ${getNotificationStyle(
                    notificationDto.type
                  )}`,
                  { "bg-gray-50 border-l-4 border-blue-500": !read }
                )}
                onClick={() =>
                  onNotificationClick(id, notificationDto.link, read)
                }
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {getEventIcon(notificationDto.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3
                          className={`text-base font-medium ${
                            !read ? "text-gray-900" : "text-gray-700"
                          }`}
                        >
                          {notificationDto.name}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                          {notificationDto.description}
                        </p>
                        <div className="mt-2 flex items-center space-x-3">
                          <span className="text-xs text-gray-500">
                            {dateTime(notificationDto.createdAt)}
                          </span>
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {getNotificationNameFromCode(notificationDto.type)}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(markNotificationAsUnread(id));
                          }}
                          className={`p-1 rounded-full hover:bg-gray-200 transition-colors ${
                            read ? "text-green-500" : "text-gray-400"
                          }`}
                          title={
                            read ? "Marquer comme non lu" : "Marquer comme lu"
                          }
                        >
                          {read ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : (
                            <Circle className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default NotificationAdminPanel;
